import {
  claimKnowledgePhraseChatPullTask,
  KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
  reportKnowledgePhraseChatPullTask,
  reportKnowledgePhraseActiveSessionDiscovery,
  submitKnowledgePhraseChatPullCandidates,
  type KnowledgePhraseCandidate,
  type KnowledgePhraseChatPullTask,
} from '@/api/modules/knowledgePhrase'
import { getChatActiveSessions, getChatMessages } from '@/api/modules/chat'
import { ipcCall } from '@/composables/useIPC'
import { logger } from '@/utils/logger'
import { isDesktopRuntime, platformListen } from '@/platform/bridge'
import { useAuthStore } from '@/stores/auth'
import { IpcCommand, TauriEvent, type TauriEventPayloadMap } from '@/types/ipc'

const CLAIM_PERMISSIONS = ['knowledgePhrase:generate', 'agentStudioKnowledge:edit'] as const
// 空闲客户端最多每 5 分钟向 Boss 领取一次，避免持续轮询占用客户电脑和解密助手。
const POLL_INTERVAL_MS = 5 * 60_000
const INACTIVE_RECHECK_MS = 5 * 60_000
const MAX_INITIAL_STAGGER_MS = 45_000
const MAX_JITTER_MS = 15_000
const CHAT_PAGE_SIZE = 100
// 每个任务最多 5 个顺序 IPC 读取，和 500 条总读取上限共同限制本机负载。
const MAX_CHAT_PAGES = 5
const MAX_CHAT_MESSAGES = 500
// 每位联系人将当前时间窗内文本按会话顺序归并为最多 5 段，再交给 AI 清洗。
const MAX_CHAT_SESSION_SEGMENTS = 5
const MAX_CHAT_SESSION_SEGMENT_CHARS = 2_000
const MAX_CHAT_MESSAGE_CHARS = 2_000
const CHAT_IPC_TIMEOUT_MS = 15_000
// 单任务最长 30 秒，分页之间主动让出事件循环，避免连续读取影响前台使用。
const CHAT_TASK_TIMEOUT_MS = 30_000
const CHAT_PAGE_YIELD_MS = 250
// 连续任务之间节流间隔，避免瞬间高频读取打满 IPC / CPU 导致界面卡顿
const TASK_COOLDOWN_MS = 1_500
// 处于提炼弹窗等活跃状态时无任务认领的重试间隔
const ACTIVE_POLL_INTERVAL_MS = 4_000

type ChatPullFailureCode =
  | 'chatservice_unavailable'
  | 'chatservice_timeout'
  | 'chatservice_rate_limited'
  | 'chatservice_error'
  | 'chatservice_pagination_incomplete'

interface ActiveChatPull {
  controller: AbortController
}

interface ChatPullReadResult {
  nextAfterId: string
  messageCount: number
  candidates: KnowledgePhraseCandidate[]
}

class ChatPullPaginationError extends Error {
  constructor() {
    super('ChatService 分页结果不完整')
    this.name = 'ChatPullPaginationError'
  }
}

class ChatPullTimeoutError extends Error {
  constructor() {
    super('企业话术聊天读取超过本机时限')
    this.name = 'ChatPullTimeoutError'
  }
}

/**
 * 企业话术本机读取调度器：
 * - 仅在桌面端且当前账号具有旧话术生成或新知识编辑权限时领取 Boss 任务；
 * - 只经 IPC -> Rust -> ChatService 读取本机聊天；
 * - 原始页面内容只在当前读取页内处理；已脱敏、限长的会话片段在本任务内存中保留至提交后立即清空；
 * - 不将聊天正文写入浏览器存储、SQLite、日志、本地文件或 V2 report。
 */
class KnowledgePhraseChatPullScheduler {
  private started = false
  private timer: number | null = null
  private scheduleVersion = 0
  private active: ActiveChatPull | null = null
  private deviceId = ''
  private extractionActive = false

  start() {
    if (this.started || !isDesktopRuntime) return
    this.started = true
    this.listenForForegroundFocus()
    void this.listenForAuthSessionChanges()
    void this.scheduleInitialRun()
  }

  private listenForForegroundFocus() {
    window.addEventListener('focus', () => {
      if (!this.started) return
      // 当处于提炼知识状态时，即使窗口聚焦也不中断，保持受控流速提取
      if (this.extractionActive) return
      this.active?.controller.abort('用户正在使用 AIHelper')
      this.scheduleNext(INACTIVE_RECHECK_MS)
    })
  }

  private async listenForAuthSessionChanges() {
    try {
      await platformListen<TauriEventPayloadMap[TauriEvent.AuthSessionChanged]>(
        TauriEvent.AuthSessionChanged,
        () => {
          this.active?.controller.abort('认证会话已变化')
          this.scheduleNext(0)
        },
      )
    } catch {
      // 认证事件监听失败不放宽权限；下一次轮询仍会重新检查当前会话。
    }
  }

  private async scheduleInitialRun() {
    const deviceId = await this.getDeviceId()
    if (!this.started) return
    const stagger = deviceId
      ? stableDeviceOffset(deviceId, MAX_INITIAL_STAGGER_MS)
      : MAX_INITIAL_STAGGER_MS
    this.scheduleNext(stagger + randomJitter())
  }

  private scheduleNext(delayMs: number) {
    if (!this.started) return
    if (this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
    const version = ++this.scheduleVersion
    this.timer = window.setTimeout(
      () => {
        this.timer = null
        void this.runOnce(version)
      },
      Math.max(0, delayMs),
    )
  }

  private async runOnce(version: number) {
    if (!this.started || version !== this.scheduleVersion) return
    if (this.active) {
      this.scheduleNext(INACTIVE_RECHECK_MS)
      return
    }
    if (this.shouldDeferForForegroundUse()) {
      this.scheduleNext(INACTIVE_RECHECK_MS)
      return
    }
    if (!this.canClaimTask()) {
      if (version === this.scheduleVersion) this.scheduleNext(INACTIVE_RECHECK_MS)
      return
    }

    const deviceId = await this.getDeviceId()
    if (!deviceId || !this.canClaimTask() || version !== this.scheduleVersion) {
      if (version === this.scheduleVersion) this.scheduleNext(INACTIVE_RECHECK_MS)
      return
    }

    let task: KnowledgePhraseChatPullTask | null = null
    const controller = new AbortController()
    // 领取请求也计入活跃任务，避免会话事件或定时器造成并发领取。
    this.active = { controller }
    try {
      task = await claimKnowledgePhraseChatPullTask(deviceId, true)
      if (
        !task ||
        controller.signal.aborted ||
        version !== this.scheduleVersion ||
        !this.isTaskInCurrentScope(task)
      ) {
        return
      }

      logger.info('KnowledgePhraseChatPull', 'chat_pull_task_claimed', {
        taskId: task.id,
        generationJobId: task.generationJobId,
        companyId: task.companyId,
        taskType: task.taskType ?? 'chat_pull',
      })

      if (task.taskType === 'active_session_discovery') {
        await this.discoverAndReportTask(task, deviceId, controller.signal)
      } else {
        await this.readAndReportTask(task, deviceId, controller.signal)
      }
    } catch (error) {
      logger.warn('KnowledgePhraseChatPull', 'chat_pull_run_failed', {
        taskId: task?.id,
        errorName: error instanceof Error ? error.name : 'unknown',
      })
      // 领取失败没有任务可安全上报；由下一轮和 Boss 的任务状态共同处理。
    } finally {
      if (this.active?.controller === controller) this.active = null
      if (version === this.scheduleVersion) {
        if (task) {
          // 刚处理完一个任务，等待节流冷却后若仍处于提炼状态则继续下一单，避免瞬时并发
          this.scheduleNext(
            this.extractionActive ? TASK_COOLDOWN_MS : POLL_INTERVAL_MS + randomJitter(),
          )
        } else {
          // 未认领到任务
          this.scheduleNext(
            this.extractionActive ? ACTIVE_POLL_INTERVAL_MS : POLL_INTERVAL_MS + randomJitter(),
          )
        }
      }
    }
  }

  private shouldDeferForForegroundUse() {
    // 处于提炼工作状态下允许受控在前台运行；否则正常避让前台操作
    if (this.extractionActive) return false
    return document.visibilityState === 'visible' && document.hasFocus()
  }

  setExtractionActive(active: boolean) {
    this.extractionActive = active
    if (active) {
      this.triggerNow()
    }
  }

  triggerNow() {
    if (!this.started) return
    if (this.active) return
    this.scheduleNext(0)
  }

  private canClaimTask() {
    const auth = useAuthStore()
    return Boolean(
      isDesktopRuntime &&
      auth.isLoggedIn &&
      Number(auth.companyId) > 0 &&
      CLAIM_PERMISSIONS.some((permission) => auth.hasPermission(permission)),
    )
  }

  private isTaskInCurrentScope(task: KnowledgePhraseChatPullTask) {
    const auth = useAuthStore()
    return this.canClaimTask() && Number(auth.companyId) === task.companyId
  }

  private async getDeviceId() {
    if (this.deviceId) return this.deviceId
    try {
      const deviceId = await ipcCall<string>(IpcCommand.DeviceGetId, undefined, {
        silent: true,
        timeout: 5_000,
      })
      this.deviceId = deviceId.trim()
    } catch {
      this.deviceId = ''
    }
    return this.deviceId
  }

  private async discoverAndReportTask(
    task: KnowledgePhraseChatPullTask,
    deviceId: string,
    signal: AbortSignal,
  ) {
    const requestId = `knowledge-phrase-discovery-${task.id}`
    const basePayload = {
      protocolVersion: KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
      deviceId,
      requestId,
      idempotencyKey: task.idempotencyKey,
      companyId: task.companyId,
      wechatId: task.wechatId,
      windowStartAt: task.windowStartAt,
      windowEndAt: task.windowEndAt,
    }

    logger.info('KnowledgePhraseChatPull', 'discovery_chatservice_started', {
      taskId: task.id,
      requestId,
      startDate: taskWindowDate(task.windowStartAt),
      endDate: taskWindowDate(task.windowEndAt),
      hasWechatId: Boolean(task.wechatId),
    })

    let response: Awaited<ReturnType<typeof getChatActiveSessions>>
    try {
      response = await getChatActiveSessions({
        wechatId: task.wechatId,
        startDate: taskWindowDate(task.windowStartAt),
        endDate: taskWindowDate(task.windowEndAt),
        taskId: task.id,
        requestId,
      })
      logger.info('KnowledgePhraseChatPull', 'discovery_chatservice_succeeded', {
        taskId: task.id,
        requestId,
        sessionCount: response.list.length,
        truncated: response.truncated,
      })
      if (signal.aborted || !this.isTaskInCurrentScope(task)) return
      if (response.truncated) {
        throw new ChatPullPaginationError()
      }
    } catch (error) {
      if (signal.aborted || !this.isTaskInCurrentScope(task)) return
      const failure = classifyChatPullFailure(error)
      logger.warn('KnowledgePhraseChatPull', 'discovery_chatservice_failed', {
        taskId: task.id,
        requestId,
        failureCode: failure.code,
        errorName: error instanceof Error ? error.name : 'unknown',
      })
      try {
        await reportKnowledgePhraseActiveSessionDiscovery(task.id, {
          ...basePayload,
          fetchStatus: 'failed',
          failureCode: failure.code,
          failureSummary: failure.summary,
        })
        logger.info('KnowledgePhraseChatPull', 'discovery_failure_reported', {
          taskId: task.id,
          requestId,
          failureCode: failure.code,
        })
      } catch (reportError) {
        logger.error('KnowledgePhraseChatPull', 'discovery_report_failed', {
          taskId: task.id,
          requestId,
          errorName: reportError instanceof Error ? reportError.name : 'unknown',
        })
        throw reportError
      }
      return
    }

    try {
      await reportKnowledgePhraseActiveSessionDiscovery(task.id, {
        ...basePayload,
        fetchStatus: 'success',
        sessions: response.list.map((session) => ({
          contactId: session.contactId,
          lastMessageAt: session.lastMessageAt,
        })),
      })
      logger.info('KnowledgePhraseChatPull', 'discovery_report_succeeded', {
        taskId: task.id,
        requestId,
        sessionCount: response.list.length,
      })
    } catch (error) {
      logger.error('KnowledgePhraseChatPull', 'discovery_report_failed', {
        taskId: task.id,
        requestId,
        errorName: error instanceof Error ? error.name : 'unknown',
      })
      throw error
    }
  }

  private async readAndReportTask(
    task: KnowledgePhraseChatPullTask,
    deviceId: string,
    signal: AbortSignal,
  ) {
    let readResult: ChatPullReadResult
    try {
      readResult = await this.readChatPages(task, signal)
    } catch (error) {
      if (signal.aborted || !this.isTaskInCurrentScope(task)) return
      const failure = classifyChatPullFailure(error)
      await reportKnowledgePhraseChatPullTask(task.id, {
        protocolVersion: KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
        deviceId,
        idempotencyKey: task.idempotencyKey,
        companyId: task.companyId,
        customerId: task.customerId,
        contactId: task.contactId,
        wechatId: task.wechatId,
        windowStartAt: task.windowStartAt,
        windowEndAt: task.windowEndAt,
        fetchStatus: 'failed',
        failureCode: failure.code,
        failureSummary: failure.summary,
      })
      return
    }

    if (signal.aborted || !this.isTaskInCurrentScope(task)) return
    try {
      if (readResult.candidates.length > 0) {
        await submitKnowledgePhraseChatPullCandidates(task.id, {
          protocolVersion: KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
          deviceId,
          idempotencyKey: task.idempotencyKey,
          companyId: task.companyId,
          customerId: task.customerId,
          contactId: task.contactId,
          wechatId: task.wechatId,
          windowStartAt: task.windowStartAt,
          windowEndAt: task.windowEndAt,
          messageCount: readResult.messageCount,
          candidates: readResult.candidates,
        })
      }
    } finally {
      clearChatPhraseCandidates(readResult.candidates)
    }

    if (signal.aborted || !this.isTaskInCurrentScope(task)) return
    await reportKnowledgePhraseChatPullTask(task.id, {
      protocolVersion: KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
      deviceId,
      idempotencyKey: task.idempotencyKey,
      companyId: task.companyId,
      customerId: task.customerId,
      contactId: task.contactId,
      wechatId: task.wechatId,
      windowStartAt: task.windowStartAt,
      windowEndAt: task.windowEndAt,
      fetchStatus: 'success',
      ...(readResult.nextAfterId ? { nextAfterId: readResult.nextAfterId } : {}),
      messageCount: readResult.messageCount,
    })
  }

  private async readChatPages(
    task: KnowledgePhraseChatPullTask,
    signal: AbortSignal,
  ): Promise<ChatPullReadResult> {
    const seenMessageIDs = new Set<string>()
    let offset = 0
    let nextAfterId = ''
    let fetchedMessageCount = 0
    let messageCount = 0
    const candidates: KnowledgePhraseCandidate[] = []
    const startDate = taskWindowDate(task.windowStartAt)
    const endDate = taskWindowDate(task.windowEndAt)
    const deadlineAt = Date.now() + CHAT_TASK_TIMEOUT_MS

    for (let page = 0; page < MAX_CHAT_PAGES; page += 1) {
      if (signal.aborted) throw abortError()
      const remaining = MAX_CHAT_MESSAGES - fetchedMessageCount
      const remainingDuration = deadlineAt - Date.now()
      if (remainingDuration <= 0) throw new ChatPullTimeoutError()
      if (remaining <= 0) throw new ChatPullPaginationError()

      const response = await getChatMessages(
        {
          contactId: task.contactId,
          wechatId: task.wechatId,
          afterId: task.afterMsgId || undefined,
          offset,
          startDate,
          endDate,
          limit: Math.min(CHAT_PAGE_SIZE, remaining),
          relaxDateOnEmpty: false,
          ephemeral: true,
        },
        {
          signal,
          timeout: Math.min(CHAT_IPC_TIMEOUT_MS, Math.max(1_000, remainingDuration)),
          silent: true,
        },
      )

      if (Date.now() > deadlineAt) throw new ChatPullTimeoutError()
      if (response.nextAfterId) nextAfterId = response.nextAfterId
      if (response.list.length === 0) {
        if (response.hasMore) throw new ChatPullPaginationError()
        return { nextAfterId, messageCount, candidates }
      }
      fetchedMessageCount += response.list.length

      const pageSize = response.list.length
      try {
        for (const message of response.list) {
          const id = message.id.trim()
          const timestamp = message.timestamp.trim()
          if (!id || !timestamp || seenMessageIDs.has(id)) throw new ChatPullPaginationError()
          seenMessageIDs.add(id)
          const content = message.content.trim()
          if (content && message.type === 'text') {
            messageCount += 1
            appendChatSessionMessage(candidates, content, message.direction)
          }
        }
      } finally {
        clearChatPage(response.list)
      }

      if (!response.hasMore) return { nextAfterId, messageCount, candidates }
      if (page === MAX_CHAT_PAGES - 1 || fetchedMessageCount >= MAX_CHAT_MESSAGES) {
        throw new ChatPullPaginationError()
      }
      await yieldBetweenChatPages(signal)
      offset += pageSize
    }

    throw new ChatPullPaginationError()
  }
}

function appendChatSessionMessage(
  candidates: KnowledgePhraseCandidate[],
  value: string,
  direction: 'sent' | 'received',
) {
  let line = `${direction === 'sent' ? '企业' : '客户'}：${value
    .slice(0, MAX_CHAT_MESSAGE_CHARS)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[已脱敏邮箱]')
    .replace(/\b1\d{10}\b/g, '[已脱敏手机号]')
    .replace(/\b(wxid|wechat)_[a-z0-9_-]{4,}\b/gi, '[已脱敏微信号]')
    .replace(/\s+/g, ' ')
    .trim()}`
  if (line.length <= 3) return

  while (line && candidates.length < MAX_CHAT_SESSION_SEGMENTS) {
    let current = candidates[candidates.length - 1]
    if (!current || current.content.length >= MAX_CHAT_SESSION_SEGMENT_CHARS) {
      current = {
        title: `当前会话窗口片段 ${candidates.length + 1}`,
        content: '',
        sceneTags: ['chat_generated'],
      }
      candidates.push(current)
    }

    const separator = current.content ? '\n' : ''
    const available = MAX_CHAT_SESSION_SEGMENT_CHARS - current.content.length - separator.length
    if (available <= 0) continue
    const part = line.slice(0, available)
    current.content += separator + part
    line = line.slice(part.length)
  }
}

function clearChatPhraseCandidates(candidates: KnowledgePhraseCandidate[]) {
  for (const candidate of candidates) {
    candidate.content = ''
    candidate.title = ''
    candidate.sceneTags = []
  }
  candidates.length = 0
}

function clearChatPage(messages: ChatMessage[]) {
  for (let index = 0; index < messages.length; index += 1) {
    messages[index] = {} as ChatMessage
  }
}

function taskWindowDate(value: string) {
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)
  if (!match) throw new ChatPullPaginationError()
  return match[1]
}

function yieldBetweenChatPages(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(done, CHAT_PAGE_YIELD_MS)
    const onAbort = () => {
      window.clearTimeout(timer)
      signal.removeEventListener('abort', onAbort)
      reject(abortError())
    }
    function done() {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

function stableDeviceOffset(deviceId: string, maxOffsetMs: number) {
  let hash = 0
  for (const char of deviceId) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  }
  return hash % Math.max(1, maxOffsetMs)
}

function randomJitter() {
  return Math.floor(Math.random() * (MAX_JITTER_MS + 1))
}

function abortError() {
  const error = new Error('任务已取消')
  error.name = 'AbortError'
  return error
}

function classifyChatPullFailure(error: unknown): { code: ChatPullFailureCode; summary: string } {
  if (error instanceof ChatPullTimeoutError) {
    return { code: 'chatservice_timeout', summary: '本机 ChatService 读取超过资源保护时限' }
  }
  if (error instanceof ChatPullPaginationError) {
    return {
      code: 'chatservice_pagination_incomplete',
      summary: '本机 ChatService 分页结果不完整',
    }
  }

  const message = error instanceof Error ? error.message.toLowerCase() : ''
  if (error instanceof Error && error.name === 'AbortError') {
    return { code: 'chatservice_timeout', summary: '本机 ChatService 读取已取消或超时' }
  }
  if (message.includes('timeout') || message.includes('超时')) {
    return { code: 'chatservice_timeout', summary: '本机 ChatService 读取超时' }
  }
  if (message.includes('429') || message.includes('rate limit') || message.includes('限流')) {
    return { code: 'chatservice_rate_limited', summary: '本机 ChatService 当前限流' }
  }
  if (
    message.includes('network') ||
    message.includes('connection') ||
    message.includes('unavailable') ||
    message.includes('not connected') ||
    message.includes('服务不可用')
  ) {
    return { code: 'chatservice_unavailable', summary: '本机 ChatService 当前不可用' }
  }
  return { code: 'chatservice_error', summary: '本机 ChatService 读取异常' }
}

const knowledgePhraseChatPullScheduler = new KnowledgePhraseChatPullScheduler()

export function startKnowledgePhraseChatPullScheduler() {
  knowledgePhraseChatPullScheduler.start()
}

/** 标记是否处于 AI 提炼聊天记录弹窗或活跃操作中，解除前台避让并以流速限制模式拉取 */
export function setKnowledgePhraseExtractionActive(active: boolean) {
  knowledgePhraseChatPullScheduler.setExtractionActive(active)
}

/** 即时触发一次聊天读取任务检查 */
export function triggerKnowledgePhraseChatPullNow() {
  knowledgePhraseChatPullScheduler.triggerNow()
}
